import { AlertCircleIcon, Trash2Icon } from "lucide-react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

type ConfirmDialogVariant = "delete" | "discard";

type ConfirmDialogProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  variant?: ConfirmDialogVariant;
};

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onDismiss,
  variant = "discard",
}: ConfirmDialogProps) {
  const config = {
    delete: {
      confirmColor: "destructive" as const,
      confirmText: "Delete",
      confirmVariant: undefined,
      description:
        "Are you sure you want to delete this/these asset(s)? This action cannot be undone.",
      icon: <Trash2Icon size={20} />,
      iconBgClass: "bg-bg-error-secondary",
      iconColorClass: "text-featured-icon-light-fg-error",
      title: "Delete file(s)",
    },
    discard: {
      confirmColor: undefined,
      confirmText: "Discard upload",
      confirmVariant: "primary" as const,
      description:
        "Are you sure you want to discard this upload? This action cannot be undone.",
      icon: <AlertCircleIcon size={20} />,
      iconBgClass: "bg-bg-warning-secondary",
      iconColorClass: "text-featured-icon-light-fg-warning",
      title: "Discard upload",
    },
  };

  const {
    confirmColor,
    confirmText,
    confirmVariant,
    description,
    icon,
    iconBgClass,
    iconColorClass,
    title,
  } = config[variant];

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable={false}
      isOpen={isOpen}
    >
      <Dialog alert>
        <DialogHeader className="flex flex-col gap-[16px]">
          <div
            className={`size-12 ${iconBgClass} rounded-full grid place-items-center ${iconColorClass}`}
          >
            {icon}
          </div>
          <span>{title}</span>
        </DialogHeader>
        <DialogBody className="pt-[4px]">
          <span className="text-sm text-text-tertiary ">{description}</span>
        </DialogBody>
        <DialogFooter className="flex gap-lg">
          <Button
            className="flex-1"
            color="gray"
            onPress={onDismiss}
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            color={confirmColor}
            onPress={onConfirm}
            variant={confirmVariant}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
